import React, { useMemo } from "react";
import "./JsonEditor.css";

type JsonEditorProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  rows?: number;
  placeholder?: string;
};

const escapeHtml = (value: string) =>
  value.replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const highlightJson = (value: string) => {
  if (!value) {
    return "";
  }
  const escaped = escapeHtml(value);
  const tokenRegex = /("(?:\\.|[^"\\])*"(?::)?|\btrue\b|\bfalse\b|\bnull\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/gi;
  return escaped.replace(tokenRegex, (match) => {
    let tokenClass = "number";
    if (match.startsWith("\"")) {
      if (match.endsWith(":")) {
        const key = match.slice(0, -1);
        return `<span class="json-editor__token json-editor__token--key">${key}</span>:`;
      }
      tokenClass = "string";
    } else if (/true|false/i.test(match)) {
      tokenClass = "boolean";
    } else if (/null/i.test(match)) {
      tokenClass = "null";
    }
    return `<span class="json-editor__token json-editor__token--${tokenClass}">${match}</span>`;
  });
};

const JsonEditor: React.FC<JsonEditorProps> = ({
  id,
  value,
  onChange,
  disabled = false,
  rows = 6,
  placeholder,
}) => {
  const highlighted = useMemo(() => highlightJson(value), [value]);
  const minHeight = Math.max(rows, 3) * 24;

  return (
    <div className={`json-editor${disabled ? " is-disabled" : ""}`} style={{ minHeight }}>
      <pre
        className="json-editor__code"
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: highlighted }}
      />
      <textarea
        id={id}
        className="json-editor__textarea"
        value={value}
        spellCheck={false}
        rows={rows}
        disabled={disabled}
        onChange={(event) => {
          if (!disabled) {
            onChange(event.target.value);
          }
        }}
      />
      {!value.trim() && placeholder && (
        <span className="json-editor__placeholder">{placeholder}</span>
      )}
    </div>
  );
};

export default JsonEditor;
